import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import type { EntityKind } from "./entity-registry";

export type PeekEntry = { kind: EntityKind; id: string };

type PeekContextValue = {
  stack: PeekEntry[];
  /** Abre un registro. Desde dentro del panel se apila sobre el actual. */
  open: (entry: PeekEntry, opts?: { push?: boolean }) => void;
  back: () => void;
  close: () => void;
};

const PeekContext = createContext<PeekContextValue | null>(null);
/** true dentro del contenido del panel: los enlaces apilan en vez de reemplazar. */
export const InsidePeekContext = createContext(false);

export function EntityPeekProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<PeekEntry[]>([]);
  const open = useCallback((entry: PeekEntry, opts?: { push?: boolean }) => {
    setStack((prev) => {
      const top = prev[prev.length - 1];
      if (top && top.kind === entry.kind && top.id === entry.id) return prev;
      return opts?.push ? [...prev, entry] : [entry];
    });
  }, []);
  const back = useCallback(() => setStack((prev) => prev.slice(0, -1)), []);
  const close = useCallback(() => setStack([]), []);
  const value = useMemo(() => ({ stack, open, back, close }), [stack, open, back, close]);
  return <PeekContext.Provider value={value}>{children}</PeekContext.Provider>;
}

export function useEntityPeek() {
  return useContext(PeekContext);
}
